import { UnexpectedError } from "../../../domain/errors/unexpected-error";
import {
  AdapterConfig,
  AdapterPreviewModel,
  AdapterSimulateModel,
  CityAdapterConfigAdmin,
  CityAdapterConfigCreateModel,
  CityAdapterConfigModel,
  CityAdapterConfigPageModel,
  CityAdapterConfigUpdateModel,
} from "../../../domain/models/city-adapter-config";
import { HttpClient, HttpStatusCode } from "../../protocols/http/http-client";

export class RemoteCityAdapterConfig implements CityAdapterConfigAdmin {
  constructor(
    private readonly url: string,
    private readonly httpClient: HttpClient
  ) {}

  async list(search: string, page = 0, size = 20): Promise<CityAdapterConfigPageModel> {
    const httpResponse = await this.httpClient.request({
      url: this.url,
      method: "get",
      params: { search, page, size },
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async getByIbge(ibge: string, layout: string): Promise<CityAdapterConfigModel> {
    const httpResponse = await this.httpClient.request({
      url: `${this.url}/${ibge}`,
      method: "get",
      params: { layout },
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async create(data: CityAdapterConfigCreateModel): Promise<CityAdapterConfigModel> {
    const httpResponse = await this.httpClient.request({
      url: this.url,
      method: "post",
      body: data,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
      case HttpStatusCode.created:
        return httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async update(ibge: string, layout: string, data: CityAdapterConfigUpdateModel): Promise<CityAdapterConfigModel> {
    const httpResponse = await this.httpClient.request({
      url: `${this.url}/${ibge}`,
      method: "put",
      body: data,
      params: { layout },
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async remove(ibge: string, layout: string): Promise<void> {
    const httpResponse = await this.httpClient.request({
      url: `${this.url}/${ibge}`,
      method: "delete",
      params: { layout },
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
      case HttpStatusCode.noContent:
        return;
      default:
        throw new UnexpectedError();
    }
  }

  async previewPayload(ibge: string, layout: string): Promise<AdapterPreviewModel> {
    const httpResponse = await this.httpClient.request({
      url: `${this.url}/${ibge}/preview-payload`,
      method: "get",
      params: { layout },
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async simulate(ibge: string, layout: string, config: AdapterConfig): Promise<AdapterSimulateModel> {
    const httpResponse = await this.httpClient.request({
      url: `${this.url}/${ibge}/simulate`,
      method: "post",
      body: { config },
      params: { layout },
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      default: {
        const msg = httpResponse.body?.messageClient || httpResponse.body?.message || "Erro na simulação.";
        throw new Error(msg);
      }
    }
  }

  async listConfiguredCities(): Promise<string[]> {
    const httpResponse = await this.httpClient.request({
      url: `${this.url}/configured-cities`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }
}
